import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";

import { AppProvider, useApp } from "@/store";
import { ShopProvider } from "@/shop";
import { AppLayout } from "@/components/layout";
import { Spinner } from "@/components/kit";
import ErrorBoundary from "@/components/ErrorBoundary";
import Welcome from "@/pages/Welcome";
import Auth from "@/pages/Auth";
import Questionnaire from "@/pages/Questionnaire";
import Home from "@/pages/Home";
import Scan from "@/pages/Scan";
import ScanResults from "@/pages/ScanResults";
import Explore from "@/pages/Explore";
import MyStyle from "@/pages/MyStyle";
import Shop from "@/pages/Shop";
import Cart from "@/pages/Cart";
import Product from "@/pages/Product";
import StyleDetail from "@/pages/StyleDetail";
import TryOn from "@/pages/TryOn";
import Share from "@/pages/Share";
import Recommendations from "@/pages/Recommendations";
import EditProfile from "@/pages/EditProfile";
import Account from "@/pages/Account";
import Privacy from "@/pages/Privacy";
import Legal from "@/pages/Legal";
import { Article, Learn } from "@/pages/Learn";
import AdminHome from "@/pages/admin/AdminHome";
import AdminBrand from "@/pages/admin/AdminBrand";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminRules from "@/pages/admin/AdminRules";
import AdminHairstyles from "@/pages/admin/AdminHairstyles";
import AdminHairstyle from "@/pages/admin/AdminHairstyle";
import AdminContent from "@/pages/admin/AdminContent";
import AdminVideos from "@/pages/admin/AdminVideos";
import AdminUsers from "@/pages/admin/AdminUsers";

/** UI guard only — every /api/admin route is enforced server-side (401/403). */
function AdminGuard({ children }) {
  const { user, ready } = useApp();
  if (!ready) return <div data-testid="admin-loading" className="flex min-h-[60vh] items-center justify-center"><Spinner /></div>;
  if (user?.role !== "admin") return <Navigate to="/mystyle" replace />;
  return children;
}

const admin = (el) => <AdminGuard>{el}</AdminGuard>;

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppProvider>
          <ShopProvider>
            <Routes>
              <Route path="/" element={<Welcome />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/questionnaire" element={<Questionnaire />} />
              <Route element={<AppLayout />}>
                <Route path="/home" element={<Home />} />
                <Route path="/scan" element={<Scan />} />
                <Route path="/explore" element={<Explore />} />
                <Route path="/mystyle" element={<MyStyle />} />
                <Route path="/shop" element={<Shop />} />
                <Route path="/scan-results" element={<ScanResults />} />
                <Route path="/cart" element={<Cart />} />
                <Route path="/product/:slug" element={<Product />} />
                <Route path="/style/:slug" element={<StyleDetail />} />
                <Route path="/try-on" element={<TryOn />} />
                <Route path="/share" element={<Share />} />
                <Route path="/recommendations" element={<Recommendations />} />
                <Route path="/edit-profile" element={<EditProfile />} />
                <Route path="/account" element={<Account />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/legal/:kind" element={<Legal />} />
                <Route path="/learn" element={<Learn />} />
                <Route path="/learn/:id" element={<Article />} />
                <Route path="/admin" element={admin(<AdminHome />)} />
                <Route path="/admin/brand" element={admin(<AdminBrand />)} />
                <Route path="/admin/products" element={admin(<AdminProducts />)} />
                <Route path="/admin/rules" element={admin(<AdminRules />)} />
                <Route path="/admin/hairstyles" element={admin(<AdminHairstyles />)} />
                <Route path="/admin/hairstyle/:slug" element={admin(<AdminHairstyle />)} />
                <Route path="/admin/content" element={admin(<AdminContent />)} />
                <Route path="/admin/videos" element={admin(<AdminVideos />)} />
                <Route path="/admin/users" element={admin(<AdminUsers />)} />
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </ShopProvider>
        </AppProvider>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
